import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic2, Upload, LogOut, Crown, Users, MessageCircle, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LikeAlertBanner from './LikeAlertBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout, onProfileUpdate }) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);

  const isPremium = user?.user?.subscription_status === 'premium';
  const hasVoiceBio = user?.has_voice_bio;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/profile/upload-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile photo uploaded!');
      onProfileUpdate();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="glass border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mic2 className="w-8 h-8 text-pink-500" />
              <h1 className="text-2xl font-bold gradient-text">Soundlyke</h1>
            </div>
            <div className="flex items-center space-x-4">
              {isPremium && (
                <div className="premium-badge flex items-center space-x-1">
                  <Crown className="w-4 h-4" />
                  <span>Premium</span>
                </div>
              )}
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Like Alerts teaser for free users */}
        {!isPremium && <LikeAlertBanner />}

        {/* Welcome Section */}
        <div className="card mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.user?.name || 'User'}!
              </h2>
              <p className="text-gray-600">Let your voice do the talking</p>
            </div>
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                {user?.user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              {user?.user?.profile_photo && (
                <img 
                  src={user.user.profile_photo} 
                  alt="Profile" 
                  className="absolute inset-0 w-20 h-20 rounded-full object-cover"
                />
              )}
            </div>
          </div>
        </div>

        {/* Setup Steps */}
        {(!hasVoiceBio || !user?.user?.profile_photo) && (
          <div className="card mb-8 bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-pink-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Sparkles className="w-6 h-6 text-pink-500 mr-2" />
              Complete Your Profile
            </h3>
            <div className="space-y-3">
              {!hasVoiceBio && (
                <div 
                  onClick={() => navigate('/record-voice')}
                  className="flex items-center justify-between p-4 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  data-testid="record-voice-step"
                >
                  <div className="flex items-center space-x-3">
                    <Mic2 className="w-5 h-5 text-pink-500" />
                    <span className="font-medium">Record your voice bio</span>
                  </div>
                  <span className="text-pink-500 font-semibold">Start →</span>
                </div>
              )}
              {!user?.user?.profile_photo && (
                <label className="flex items-center justify-between p-4 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                       data-testid="upload-photo-step">
                  <div className="flex items-center space-x-3">
                    <Upload className="w-5 h-5 text-pink-500" />
                    <span className="font-medium">
                      {uploading ? 'Uploading...' : 'Upload profile photo'}
                    </span>
                  </div>
                  <span className="text-pink-500 font-semibold">Upload →</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Action Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Discover */}
          <button
            onClick={() => navigate('/discover')}
            disabled={!hasVoiceBio}
            className="card text-left hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="discover-btn"
          >
            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-pink-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Discover</h3>
            <p className="text-gray-600">Listen to voice bios and find your match</p>
            {!hasVoiceBio && (
              <p className="text-sm text-pink-500 mt-2">Complete your voice bio first</p>
            )}
          </button>

          {/* Matches */}
          <button
            onClick={() => navigate('/matches')}
            className="card text-left hover:shadow-xl transition-all"
            data-testid="matches-btn"
          >
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-purple-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Matches</h3>
            <p className="text-gray-600">View your connections and start chatting</p>
          </button>

          {/* Upgrade */}
          {!isPremium && (
            <button
              onClick={() => navigate('/upgrade')}
              className="card text-left hover:shadow-xl transition-all bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200"
              data-testid="upgrade-btn"
            >
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Go Premium</h3>
              <p className="text-gray-600">Unlock profile photos and chat with matches</p>
              <p className="text-sm font-semibold text-yellow-600 mt-2">From $9.99/week</p>
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;